import { t } from 'i18next';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { getLabelOptions } from '../../../constants/label-configuration-options';
import type { KpiTargetConfiguration, WidgetConfigurationData } from '../../../redux/config/widgetApi';
import { includeLegend } from '../../Charts/generalFunctions/general-functions';
import type { DefaultValueProps } from '../widget-settings-dialog';
import type { LabelFormatType } from '../widget-settings-validation';

const labelFormatDefaultValue: LabelFormatType = { value: 'percentage', text: t('percentage') };

/** Module-level constant to avoid repeated NODE_ENV checks */
const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * Maps an array of primitive values (strings, numbers) to LabelFormatType dropdown options.
 * Non-primitive values (objects, arrays) are filtered out to prevent "[object Object]" labels.
 * Logs a warning in development when non-primitives are encountered, as this likely indicates
 * a bug in the calling code.
 * @internal Exported for testing purposes
 */
export const mapToDropDownResult = (items: unknown[]): LabelFormatType[] => {
  const result: LabelFormatType[] = [];
  const nonPrimitives: unknown[] | null = IS_DEV ? [] : null;

  for (const item of items) {
    if (typeof item === 'string' || typeof item === 'number') {
      result.push({ value: String(item), text: String(item) });
    } else if (nonPrimitives) {
      nonPrimitives.push(item);
    }
  }

  if (nonPrimitives && nonPrimitives.length > 0) {
    console.warn(
      '[useWidgetConfigs] mapToDropDownResult received non-primitive values that were filtered out:',
      nonPrimitives,
      'This may indicate a data structure issue in the widget configuration.'
    );
  }

  return result;
};

const getKpiTargets = (configKPI?: KpiTargetConfiguration) => {
  if (configKPI) {
    return { ...configKPI };
  }
};

export function useWidgetConfigs(widgetConfiguration?: WidgetConfigurationData) {
  return useMemo<DefaultValueProps>(() => {
    if (!widgetConfiguration) {
      return {
        labelFormat: labelFormatDefaultValue,
        labelDecimals: 0,
      };
    }

    const displayLegend = includeLegend(widgetConfiguration);
    const { widgetConfig, widgetData, injuryFrequencyAIConfig, hoursWorkedAIConfig } = widgetConfiguration;

    const labelFormat =
      getLabelOptions().find((item) => item.value === widgetConfig?.labelFormat) ?? labelFormatDefaultValue;

    const labelPlacement = widgetConfig?.labelPlacement;
    const labelDecimals = widgetConfig?.labelDecimals ?? 0;
    const showTop = widgetConfig?.showTop;
    const kpiTargetConfiguration = getKpiTargets(widgetConfig?.kpiTargetConfiguration);
    const secondColorEnabled = widgetConfig?.secondColorEnabled;
    const displayDecimalPlaces = widgetConfig?.displayDecimalPlaces;
    const kpiTargetForColumnKpiPercentage = widgetConfig?.kpiTargetForColumnKpiPercentage;
    const colors = widgetConfig?.colors;
    const sortOrder = widgetConfig?.sortOrder;
    const globalFilter =
      widgetData.globalFilters && widgetData.globalFilters?.length > 0 ? widgetData?.globalFilters[0] : undefined;
    const notBlank = globalFilter?.notBlank === undefined ? false : globalFilter.notBlank;
    const globalFilters = globalFilter ? { value: globalFilter.id, ...globalFilter } : undefined;
    const hideBlankCategory = widgetConfig?.hideBlankCategory;

    let injuryFrequencyGroupingCriteria: LabelFormatType[] = [];
    let injuryFrequencyFields: LabelFormatType[] = [];
    let injuryFrequencyInjuryClassificationFieldName: LabelFormatType | undefined = undefined;
    let injuryFrequencyInjuryClassificationValues: LabelFormatType[] = [];

    let hoursWorkedGroupingCriteria: LabelFormatType[] = [];
    let hoursWorkedFields: LabelFormatType[] = [];
    let hoursWorkedInjuryClassificationFieldName: LabelFormatType | undefined = undefined;
    let hoursWorkedFieldName: LabelFormatType | undefined = undefined;

    if (injuryFrequencyAIConfig) {
      injuryFrequencyGroupingCriteria = mapToDropDownResult(
        Array.isArray(injuryFrequencyAIConfig.groupingCriteria) ? injuryFrequencyAIConfig.groupingCriteria : []
      );

      injuryFrequencyFields = mapToDropDownResult(
        Array.isArray(injuryFrequencyAIConfig.fields) ? injuryFrequencyAIConfig.fields : []
      );

      injuryFrequencyInjuryClassificationFieldName = injuryFrequencyAIConfig.injuryClassificationFieldName
        ? {
            value: injuryFrequencyAIConfig.injuryClassificationFieldName as string,
            text: injuryFrequencyAIConfig.injuryClassificationFieldName as string,
          }
        : undefined;
      injuryFrequencyInjuryClassificationValues = mapToDropDownResult(
        Array.isArray(injuryFrequencyAIConfig.injuryClassificationValues)
          ? injuryFrequencyAIConfig.injuryClassificationValues
          : []
      );
    }

    if (hoursWorkedAIConfig) {
      hoursWorkedGroupingCriteria = mapToDropDownResult(
        Array.isArray(hoursWorkedAIConfig.groupingCriteria) ? hoursWorkedAIConfig.groupingCriteria : []
      );

      hoursWorkedFields = mapToDropDownResult(
        Array.isArray(hoursWorkedAIConfig.fields) ? hoursWorkedAIConfig.fields : []
      );

      hoursWorkedInjuryClassificationFieldName = hoursWorkedAIConfig.injuryClassificationFieldName
        ? {
            value: hoursWorkedAIConfig.injuryClassificationFieldName as string,
            text: hoursWorkedAIConfig.injuryClassificationFieldName as string,
          }
        : undefined;
      hoursWorkedFieldName = hoursWorkedAIConfig.hoursWorkedFieldName
        ? {
            value: hoursWorkedAIConfig.hoursWorkedFieldName as string,
            text: hoursWorkedAIConfig.hoursWorkedFieldName as string,
          }
        : undefined;
    }

    const yScaleMin = !isNil(widgetConfig?.yScaleMin) ? String(widgetConfig?.yScaleMin) : '';
    const yScaleMax = !isNil(widgetConfig?.yScaleMax) ? String(widgetConfig?.yScaleMax) : '';

    return {
      form: widgetData?.form,
      field: widgetData?.field,
      secondField: widgetData?.secondField,
      module: widgetData?.module,
      notBlank,
      globalFilters,
      displayLegend,
      labelFormat,
      labelPlacement,
      labelDecimals,
      showTop,
      kpiTargetConfiguration,
      secondColorEnabled,
      displayDecimalPlaces,
      kpiTargetForColumnKpiPercentage,
      colors,
      sortOrder,
      hideBlankCategory,
      injuryFrequencyGroupingCriteria,
      injuryFrequencyFields,
      injuryFrequencyInjuryClassificationFieldName,
      injuryFrequencyInjuryClassificationValues,
      hoursWorkedGroupingCriteria,
      hoursWorkedFields,
      hoursWorkedInjuryClassificationFieldName,
      hoursWorkedFieldName,
      yScaleMin,
      yScaleMax,
    };
  }, [widgetConfiguration]);
}
